import React, { useMemo } from "react";
import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { dateLocale } from "i18n";
import { positionLabel } from "v2/constants/positions";

const getInitials = (name) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const formatShortDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "short",
  });
};

const AttendancePlayerDetail = ({ player, sessions }) => {
  const { t } = useTranslation();
  // Compute streaks from sessions sorted by date ascending
  const { currentStreak, bestStreak } = useMemo(() => {
    if (!sessions || sessions.length === 0) {
      return { currentStreak: 0, bestStreak: 0 };
    }

    const sorted = [...sessions].sort(
      (a, b) =>
        new Date(a.date || a.created_at) - new Date(b.date || b.created_at)
    );

    let best = 0;
    let running = 0;

    for (const s of sorted) {
      if (s.present) {
        running++;
        best = Math.max(best, running);
      } else {
        running = 0;
      }
    }

    return { currentStreak: running, bestStreak: best };
  }, [sessions]);

  if (!player) return null;

  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#E07A3A]">
            <span className="text-sm font-semibold text-white">
              {getInitials(player.name)}
            </span>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#111827]">
              {player.name}
            </h3>
            <p className="text-[11px] text-[#9CA3AF]">
              {positionLabel(player.position)}
            </p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-[28px] font-bold text-[#E07A3A]">
            {player.rate}%
          </span>
          <p className="text-[11px] text-[#9CA3AF]">
            {t("team.attendanceDetail.attendance")}
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3 border-b border-[#F3F4F6] px-4 py-3">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
            {t("team.attendanceDetail.present")}
          </p>
          <p className="mt-0.5 text-lg font-bold text-[#111827]">
            {player.present}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
            {t("team.attendanceDetail.absent")}
          </p>
          <p className="mt-0.5 text-lg font-bold text-[#111827]">
            {player.absent}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
            {t("team.attendanceDetail.currentStreak")}
          </p>
          <p className="mt-0.5 text-lg font-bold text-[#111827]">
            {currentStreak}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
            {t("team.attendanceDetail.bestStreak")}
          </p>
          <p className="mt-0.5 text-lg font-bold text-[#111827]">
            {bestStreak}
          </p>
        </div>
      </div>

      {/* Session Timeline */}
      <div className="px-4 py-3">
        <p className="mb-2 text-xs font-medium text-[#6B7280]">
          {t("team.attendanceDetail.sessionTimeline")}
        </p>
        {sessions && sessions.length > 0 ? (
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {[...sessions]
              .sort(
                (a, b) =>
                  new Date(a.date || a.created_at) -
                  new Date(b.date || b.created_at)
              )
              .map((s, i) => (
                <div
                  key={s.id || i}
                  className="flex flex-shrink-0 flex-col items-center gap-1"
                >
                  {s.present ? (
                    <span className="flex h-6 w-6 items-center justify-center rounded bg-[#FEF3E7]">
                      <Check size={11} className="text-[#E07A3A]" />
                    </span>
                  ) : (
                    <span className="flex h-6 w-6 items-center justify-center rounded bg-[#F3F4F6]">
                      <X size={11} className="text-[#9CA3AF]" />
                    </span>
                  )}
                  <span className="text-[9px] text-[#9CA3AF]">
                    {formatShortDate(s.date || s.created_at)}
                  </span>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-xs text-[#9CA3AF]">
            {t("team.attendanceChart.emptyTitle")}
          </p>
        )}
      </div>
    </div>
  );
};

export default AttendancePlayerDetail;
